import { ApolloQueryResult } from '@apollo/client';
import _ from 'lodash';
import React from 'react';

import {
  analytics as commonAnalytics,
  date as commonDate,
  finleyAssert,
  typeguards as commonTypeguards,
  utils as commonUtils,
} from '@finley-technologies/common';
import {
  CreditAgreement,
  Insights,
  Util,
} from '@finley-technologies/finley-types';

import {
  AggregateBorrowingRiskInsightsGqlDataGQLType,
  ConcentrationLimitAggregateInsightsGqlDataGQLType,
  ConcentrationLimitInsightsGqlDataGQLType,
  CoreBorrowingBaseInsightsGqlDataGQLType,
  CreditFacilityInsightsParamInputGQLType,
  CreditFacilityProfitVintageInputInsightsGqlDataGQLType,
  EligibilityAggregateInsightsGqlDataGQLType,
  FinancialCovenantsCalculationGQLType,
  LedgerTransactionGQLType,
  OrganizationInsightsParamInputGQLType,
  TAccountTypeGQLType,
} from 'types/dashboard-core-gqlTypes.generated';

import { colors, transparency } from 'packages/common-frontend/colors';
import {
  getDefaultDashboardDisplayEndDate,
  getDefaultDashboardDisplayStartDate,
} from 'packages/common-frontend/constants';
import {
  numberToFormattedRounded,
  numberToPercentageLabel,
} from 'packages/common-frontend/utils';
import { parseInterestRateDisplayForCapitalProvider } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/utils';

/**
 * Contains all portfolio chart utils. Cmd+F to find sections:
 * - SHARED CHART UTILS
 * - ELIGIBILITY CHART UTILS
 * - COVENANTS CHART UTILS
 * - CONCENTRATION CHART UTILS
 * - EXPENSES & FEES CHART UTILS
 * - VINTAGE CHART UTILS
 * - RISK CHART UTILS
 */

// **************************
// *** SHARED CHART UTILS ***
// **************************

export const chartsInitialEndDateJs = commonDate.fullDateStrToDate(
  getDefaultDashboardDisplayEndDate(),
);
export const chartsInitialStartDateJs = commonDate.fullDateStrToDate(
  getDefaultDashboardDisplayStartDate(),
);
export const chartsInitialStartDateMillis = commonDate.fullDateStrToEpoch(
  getDefaultDashboardDisplayStartDate(),
);
export const chartsInitialEndDateMillis = commonDate.fullDateStrToEpoch(
  getDefaultDashboardDisplayEndDate(),
);

// covenant init conditions
// currently same as other charts
export const covenantsInitialStartDate = getDefaultDashboardDisplayStartDate();
export const covenantsInitialStartDateJs = commonDate.fullDateStrToDate(
  covenantsInitialStartDate,
);
export const covenantsInitialStartDateMillis = commonDate.fullDateStrToEpoch(
  covenantsInitialStartDate,
);

// vintage init conditions
// currently same as other charts
export const vintageInitialStartDate = getDefaultDashboardDisplayStartDate();
export const vintageInitialStartDateJs = commonDate.fullDateStrToDate(
  vintageInitialStartDate,
);

export enum ChartTimePeriod {
  day = 'day',
  week = 'week',
  month = 'month',
  quarter = 'quarter',
  year = 'year',
}
export const selectedTimePeriodToLabelMap: Record<ChartTimePeriod, string> = {
  day: 'D',
  week: 'W',
  month: 'M',
  quarter: 'Q',
  year: 'Y',
};

export const defaultSelectedTimePeriod: ChartTimePeriod = ChartTimePeriod.week;

export type CoreBorrowingBaseInsightsKeys = keyof CoreBorrowingBaseInsightsGqlDataGQLType;

export type PortfolioChartFormat =
  | 'currency'
  | 'percentage'
  | 'ratio'
  | 'boolean'
  | 'integer'
  | 'float';
export type PortfolioChartComponentType =
  | 'line'
  | 'dashedLine'
  | 'bar'
  | 'area'
  | 'none';

export const selectedChartComponentTypeToFAIconMap: Partial<
  Record<PortfolioChartComponentType, string>
> = {
  line: 'chart-line',
  bar: 'chart-column',
};

export type PortfolioSingleChartConfig = {
  chartType: PortfolioChartComponentType;
  field: string;
  label: string;
  color: string;
  format: PortfolioChartFormat;
  stackId?: string; // identifies bar charts that are stacked together in the same stack
  activeColor?: string; // if the chart component's color should be different when selected / hovered
  conditionalStyle?: {
    condition: (datapoint: any) => boolean;
    style: { [key: string]: any };
  };
  defaultInactive?: boolean;
};
export type PortfolioChartConfig<T extends string> = Partial<
  Record<T, PortfolioSingleChartConfig>
>;

export type ConcentrationLimitMappedData = {
  [key: Util.FullDateStr]: {
    [key: string]: ConcentrationLimitInsightsGqlDataGQLType;
  };
};

export const portfolioFormatterMap: {
  [key in PortfolioChartFormat]: (value: any) => string;
} = {
  currency: (value) =>
    commonUtils.numberToCurrencyWithSymbol({
      number: value,
    }) ?? '-',
  percentage: (value) =>
    _.isNumber(value)
      ? commonUtils.numberToFormattedString(value, 'percentage', false)
      : '-',
  ratio: (value) => `${value.toFixed(2)}x`,
  boolean: (value) => value.toString(),
  float: (value) => value.toFixed(2),
  integer: (value) => value.toString(),
};

export const getInitialActiveFields = ({
  chartConfig,
}: {
  chartConfig: PortfolioChartConfig<any>;
}): string[] => {
  return _.compact(
    _.map(chartConfig, (singleConfig, key) => {
      return !singleConfig?.defaultInactive && key;
    }),
  );
};

export const parsePortfolioSummaryPropertiesFromCreditAgreement = (
  creditAgreementArtifact: CreditAgreement.DomainEntity.CreditAgreementFullArtifact,
  organizationIds: string[],
): {
  closingDate: Util.FullDateStr;
  maturityDate: Util.FullDateStr;
  interestRate: string | undefined;
} => {
  const {
    closingDate: { value: closingDate },
    finalMaturityDate: { value: maturityDate },
    parties: { capitalProviders },
  } = creditAgreementArtifact.facilityMechanics;

  const capitalProviderMatches = _.filter(capitalProviders, (cp) =>
    _.includes(organizationIds, cp.entity.id),
  );

  finleyAssert(
    capitalProviderMatches.length,
    `Capital provider entity for [organizations ${JSON.stringify(
      organizationIds,
    )}] must exist on credit agreement for [credit facility: ${
      creditAgreementArtifact.creditFacilityId
    }].`,
  );

  const interestRates = _.map(capitalProviderMatches, (capitalProvider) => {
    const parsedInterestRate = parseInterestRateDisplayForCapitalProvider(
      capitalProvider,
    );
    return capitalProviderMatches.length > 1
      ? `${capitalProvider.entity.name}: ${parsedInterestRate}`
      : parsedInterestRate;
  });

  return {
    closingDate,
    maturityDate,
    interestRate: _.join(interestRates, '\n'),
  };
};

export const formatDataByTimePeriod = <
  T extends { [key: string]: Util.Primitive }
>({
  chartData,
  selectedTimePeriod,
  dateFieldName,
}: {
  chartData: T[];
  selectedTimePeriod: ChartTimePeriod;
  dateFieldName: string;
}): T[] => {
  let borrowingBaseDataMap: _.Dictionary<T> = {};
  switch (selectedTimePeriod) {
    case ChartTimePeriod.week:
      borrowingBaseDataMap = _.keyBy(chartData, (dataRow) => {
        const date = dataRow[dateFieldName] as Util.FullDateStr;
        const week = commonDate.fullDateStrToWeekNumber(date);
        const year = commonDate.fullDateStrToDate(date).getFullYear();
        return `${year}-${week}`;
      });
      break;
    case ChartTimePeriod.month:
      borrowingBaseDataMap = _.keyBy(chartData, (dataRow) => {
        const date = dataRow[dateFieldName] as Util.FullDateStr;
        const month = commonDate.fullDateStrToDate(date).getMonth();
        const year = commonDate.fullDateStrToDate(date).getFullYear();
        return `${year}-${month}`;
      });
      break;

    case ChartTimePeriod.quarter:
      borrowingBaseDataMap = _.keyBy(chartData, (dataRow) => {
        const date = dataRow[dateFieldName] as Util.FullDateStr;
        const quarter = commonDate.fullDateStrToQuarter(date);
        const year = commonDate.fullDateStrToDate(date).getFullYear();
        return `${year}-${quarter}`;
      });
      break;

    case ChartTimePeriod.year:
      borrowingBaseDataMap = _.keyBy(chartData, (dataRow) => {
        const date = dataRow[dateFieldName] as Util.FullDateStr;
        return commonDate.fullDateStrToDate(date).getFullYear();
      });
      break;

    case ChartTimePeriod.day:
    default:
      borrowingBaseDataMap = _.keyBy(chartData, (dataRow) => {
        return dataRow[dateFieldName] as Util.FullDateStr;
      });
      break;
  }

  return Object.values(borrowingBaseDataMap);
};

export const formatChartDataByReportingDate = <
  T extends { reportingDate: Util.FullDateStr; [key: string]: Util.Primitive }
>({
  chartData,
  selectedTimePeriod,
}: {
  chartData: T[];
  selectedTimePeriod: ChartTimePeriod;
}): T[] => {
  const orderedChartData: T[] = _.orderBy(
    chartData,
    ['reportingDate', 'fileVersion'],
    ['asc', 'desc'],
  );

  return formatDataByTimePeriod({
    chartData: orderedChartData,
    selectedTimePeriod,
    dateFieldName: 'reportingDate',
  });
};

export const currencyFormatter = (value: any) => {
  if (value === 0) {
    return '$0';
  }
  if (typeof value === 'number') {
    return `$${numberToFormattedRounded(value)}`;
  }
  return value.toString();
};

export const percentageFormatter = (value: any) => {
  if (typeof value === 'number') {
    return numberToPercentageLabel(value);
  }
  return value.toString();
};

export const ratioFormatter = (value: any) => {
  if (typeof value === 'number') {
    return `${value.toFixed(2)}x`;
  }
  return value.toString();
};

export const basicNumberFormatter = (value: any) => {
  if (typeof value === 'number') {
    return numberToFormattedRounded(value);
  }
  return value.toString();
};

export const valueTypeToFormatterMap: {
  [key in PortfolioChartFormat]: (value: any) => string;
} = {
  currency: currencyFormatter,
  percentage: percentageFormatter,
  ratio: ratioFormatter,
  boolean: (value: any) => value.toString(),
  integer: (value: any) => value.toString(),
  float: (value: any) => value.toFixed(2),
};

export const valueTypeToTickFormatterMap: {
  [key in PortfolioChartFormat]: (value: any) => string;
} = {
  currency: currencyFormatter,
  percentage: percentageFormatter,
  ratio: ratioFormatter,
  boolean: (value: any) => value.toString(),
  integer: basicNumberFormatter,
  float: basicNumberFormatter,
};

export const dateFormatter = ({
  date,
  timePeriod,
}: {
  date: any;
  timePeriod: ChartTimePeriod;
}) => {
  if (!commonTypeguards.isFullDateStr(date)) {
    return date;
  }
  switch (timePeriod) {
    case ChartTimePeriod.day:
      return commonDate.fullDateStrToStr(date, 'LLL d');
    case ChartTimePeriod.week:
      return commonDate.fullDateStrToStr(date, 'LLL d');
    case ChartTimePeriod.month:
      return `${commonDate.fullDateStrToStr(
        date,
        'LLL',
      )} '${commonDate.fullDateStrToStr(date, 'yy')}`;
    case ChartTimePeriod.quarter:
      return `Q${commonDate.fullDateStrToQuarter(
        date,
      )} '${commonDate.fullDateStrToStr(date, 'yy')}`;
    case ChartTimePeriod.year:
      return commonDate.fullDateStrToStr(date, 'yyyy');
    default:
      return commonDate.fullDateStrToHumanDateString(date);
  }
};

export const tooltipFormatter = (
  value: number,
  name: string,
  valueType: PortfolioChartFormat,
): string[] => {
  const formatter = valueTypeToFormatterMap[valueType];
  return [formatter(value), _.startCase(name)];
};

export const formatCsvDownloadDataRows = <
  T extends { reportingDate: Util.FullDateStr; [key: string]: Util.Primitive }
>(
  chartData: T[],
  activeFields: (keyof T)[],
): { dataRows: any[]; headers: { label: string; key: string }[] } => {
  // we always want to show reportingDate in a csv row
  const dataRows = _.map(chartData, (datapoint) => {
    return _.pickBy(
      datapoint,
      (_value, key) => _.includes(activeFields, key) || key === 'reportingDate',
    );
  });

  const headers = _.map(_.keys(dataRows[0]), (headerKey) => {
    return {
      label: commonUtils.toTitleCase(headerKey),
      key: headerKey,
    };
  });

  return { dataRows, headers };
};

export const findChartDataForReportingDate = <
  T extends { reportingDate: Util.FullDateStr; [key: string]: Util.Primitive }
>(
  chartData: T[],
  reportingDate: Util.FullDateStr,
): T | undefined => {
  return _.find(
    chartData,
    (chartDatapoint) => chartDatapoint.reportingDate === reportingDate,
  );
};

export const onToggleActiveField = <T>({
  selectedField,
  currentActiveFields,
  setActiveFields,
}: {
  selectedField: keyof T;
  currentActiveFields: (keyof T)[];
  setActiveFields: (value: React.SetStateAction<(keyof T)[]>) => void;
}): void => {
  let newActiveFields = [...currentActiveFields];

  if (!_.includes(newActiveFields, selectedField)) {
    newActiveFields.push(selectedField);
  } else {
    newActiveFields = _.filter(
      newActiveFields,
      (field) => field !== selectedField,
    );
  }

  setActiveFields(newActiveFields);
};

// @dani portfolio monitoring - may want to abstract or create new component for organizationalInsights queries
export const onSelectDateRange = <T>({
  newDateRange,
  refetch,
  creditFacilityId,
  setChartDataDateRange,
  refetchVariables,
}: {
  newDateRange: [Date | null, Date | null];
  refetch: (variables: { [key: string]: any }) => Promise<ApolloQueryResult<T>>;
  creditFacilityId: string;
  setChartDataDateRange: (
    value: React.SetStateAction<{
      chartDataStartDate: Date;
      chartDataEndDate: Date;
    }>,
  ) => void;
  refetchVariables?: { [key: string]: any };
}) => {
  const [newStartDate, newEndDate] = newDateRange;

  if (newStartDate && newEndDate) {
    const defaultRefetchVariables: {
      creditFacilityParams: CreditFacilityInsightsParamInputGQLType;
    } = {
      creditFacilityParams: {
        creditFacilityId,
        reportingDateStart: newStartDate
          ? commonDate.dateToFullDateString(newStartDate)
          : getDefaultDashboardDisplayStartDate(),
        reportingDateEnd: newEndDate
          ? commonDate.dateToFullDateString(newEndDate)
          : getDefaultDashboardDisplayEndDate(),
      },
    };

    refetch(refetchVariables ?? defaultRefetchVariables);
    setChartDataDateRange({
      chartDataStartDate: newStartDate,
      chartDataEndDate: newEndDate,
    });
  }

  // if dates cleared, pass in the initial reporting date conditions
  if (!newStartDate && !newEndDate) {
    const defaultRefetchVariables: {
      creditFacilityParams: CreditFacilityInsightsParamInputGQLType;
    } = {
      creditFacilityParams: {
        creditFacilityId,
        reportingDateStart: getDefaultDashboardDisplayStartDate(),
        reportingDateEnd: getDefaultDashboardDisplayEndDate(),
      },
    };
    refetch(refetchVariables ?? defaultRefetchVariables);
    setChartDataDateRange({
      chartDataStartDate: chartsInitialStartDateJs,
      chartDataEndDate: chartsInitialEndDateJs,
    });
  }
};

export const onSelectDateRangeForOrganization = <T>({
  newDateRange,
  refetch,
  organizationId,
  setChartDataDateRange,
  refetchVariables,
}: {
  newDateRange: [Date | null, Date | null];
  refetch: (variables: { [key: string]: any }) => Promise<ApolloQueryResult<T>>;
  organizationId: string;
  setChartDataDateRange: (
    value: React.SetStateAction<{
      chartDataStartDate: Date;
      chartDataEndDate: Date;
    }>,
  ) => void;
  refetchVariables?: { [key: string]: any };
}) => {
  const [newStartDate, newEndDate] = newDateRange;

  if (newStartDate && newEndDate) {
    const defaultRefetchVariables: {
      organizationParams: OrganizationInsightsParamInputGQLType;
    } = {
      organizationParams: {
        organizationId,
        reportingDateStart: newStartDate
          ? commonDate.dateToFullDateString(newStartDate)
          : getDefaultDashboardDisplayStartDate(),
        reportingDateEnd: newEndDate
          ? commonDate.dateToFullDateString(newEndDate)
          : getDefaultDashboardDisplayEndDate(),
      },
    };

    refetch(refetchVariables ?? defaultRefetchVariables);
    setChartDataDateRange({
      chartDataStartDate: newStartDate,
      chartDataEndDate: newEndDate,
    });
  }

  // if dates cleared, pass in the initial reporting date conditions
  if (!newStartDate && !newEndDate) {
    const defaultRefetchVariables: {
      organizationParams: OrganizationInsightsParamInputGQLType;
    } = {
      organizationParams: {
        organizationId,
        reportingDateStart: getDefaultDashboardDisplayStartDate(),
        reportingDateEnd: getDefaultDashboardDisplayEndDate(),
      },
    };
    refetch(refetchVariables ?? defaultRefetchVariables);
    setChartDataDateRange({
      chartDataStartDate: chartsInitialStartDateJs,
      chartDataEndDate: chartsInitialEndDateJs,
    });
  }
};

// *******************************
// *** ELIGIBILITY CHART UTILS ***
// *******************************

export type ExpandedEligibilityAggregateInsightsData = Omit<
  EligibilityAggregateInsightsGqlDataGQLType,
  'eligibilityPropertiesData'
> & {
  [key: string]: Util.Primitive;
};

export const expandEligibilityKickoutsData = (
  rawData: EligibilityAggregateInsightsGqlDataGQLType[],
): ExpandedEligibilityAggregateInsightsData[] => {
  return _.map(
    rawData,
    ({
      reportingDate,
      totalEligibleExposure,
      totalNetEligibilityKickout,
      totalGrossEligibilityKickout,
      eligibilityPropertiesData,
    }) => {
      const expandedDataForReportingDate: ExpandedEligibilityAggregateInsightsData = {
        reportingDate,
        totalEligibleExposure,
        totalNetEligibilityKickout,
        totalGrossEligibilityKickout,
      };
      _.forEach(
        eligibilityPropertiesData,
        ({ propertyKey, totalEligibilityKickout }) => {
          expandedDataForReportingDate[propertyKey] = totalEligibilityKickout;
        },
      );
      return expandedDataForReportingDate;
    },
  );
};

// the keys that should not be displayed as individual eligibile kickout properties
export const eligiblityKickoutsChartSharedKeys: (keyof EligibilityAggregateInsightsGqlDataGQLType)[] = [
  'reportingDate',
  'totalEligibleExposure',
  'totalNetEligibilityKickout',
  'totalGrossEligibilityKickout',
];

// *****************************
// *** COVENANTS CHART UTILS ***
// *****************************

export type ExpandedFinancialCovenantsCalculation = FinancialCovenantsCalculationGQLType & {
  isPassed: boolean;
};

const calculateFinancialCovenantIsPassed = (
  financialCovenantCalculation: FinancialCovenantsCalculationGQLType,
) => {
  const { limitType, limitValue, value } = financialCovenantCalculation;

  switch (limitType) {
    case 'max':
      if (value <= limitValue) {
        return true;
      }
      break;
    case 'min':
      if (value >= limitValue) {
        return true;
      }
      break;
    default:
      return false;
  }
  return false;
};

export const addIsPassedStatusToFinancialCovenantCalculations = (
  financialCovenantCalculations: FinancialCovenantsCalculationGQLType[],
): ExpandedFinancialCovenantsCalculation[] => {
  return _.map(financialCovenantCalculations, (calc) => {
    return {
      ...calc,
      isPassed: calculateFinancialCovenantIsPassed(calc),
    };
  });
};

// *********************************
// *** CONCENTRATION CHART UTILS ***
// *********************************

/**
 * returns an array of conconcentration limit insights
 * that corresponds to the passed in concentration limit property key
 * with the reporting date attached to each data point
 */
export const formatConcentrationLimitDataForKey = (
  concentrationInsights: ConcentrationLimitAggregateInsightsGqlDataGQLType[],
  concentrationLimitKey: string,
): ConcentrationLimitInsightsGqlDataGQLType[] => {
  return concentrationInsights.reduce<
    ConcentrationLimitInsightsGqlDataGQLType[]
  >((targetData, { concentrationLimitsData }) => {
    const targetConcentrationLimit = _.find(
      concentrationLimitsData,
      ({ propertyKey }) => propertyKey === concentrationLimitKey,
    );
    if (targetConcentrationLimit) {
      targetData.push(targetConcentrationLimit);
    }
    return targetData;
  }, []);
};

// for every reporting date,
// this will contain an additional property for every concentration limit key and its kickout
export type ExpandedConcentrationAggregateInsightsData = Omit<
  ConcentrationLimitAggregateInsightsGqlDataGQLType,
  'concentrationLimitsData'
> & {
  [key: string]: Util.Primitive;
};

// the keys that should not be displayed as individual excess concentration properties
export const excessConcentrationChartSharedKeys: (keyof ConcentrationLimitAggregateInsightsGqlDataGQLType)[] = [
  'reportingDate',
  'concentrationLimitsData',
  'totalConcentrationLimitKickout',
];

/**
 * @returns an array of objects for each reporting date where each object includes key-value pairs for every concentrationLimitKey: totalExcessConcentrationAmount
 */
export const expandConcentrationLimitAggregateInsights = (
  rawData: ConcentrationLimitAggregateInsightsGqlDataGQLType[],
): ExpandedConcentrationAggregateInsightsData[] => {
  return _.map(
    rawData,
    ({
      reportingDate,
      totalConcentrationLimitKickout,
      concentrationLimitsData,
    }) => {
      const expandedDataForReportingDate: ExpandedConcentrationAggregateInsightsData = {
        reportingDate,
        totalConcentrationLimitKickout,
      };
      _.forEach(
        concentrationLimitsData,
        ({
          propertyKey,
          totalConcentrationLimitKickout: individualConcentrationLimitKickout,
        }) => {
          expandedDataForReportingDate[
            propertyKey
          ] = individualConcentrationLimitKickout;
        },
      );
      return expandedDataForReportingDate;
    },
  );
};

/**
 * Given raw data from the concentration insights gql query
 * @returns individual concentration insights data that is keyed first by reportingDate, then by propertyKey.
 * For easy lookup when rendering the details table.
 */
export const formatConcentrationDataByReportingDateAndKey = (
  rawData: ConcentrationLimitAggregateInsightsGqlDataGQLType[],
): ConcentrationLimitMappedData => {
  return _.chain(rawData)
    .keyBy('reportingDate')
    .mapValues((dataForReportingDate) =>
      _.keyBy(dataForReportingDate.concentrationLimitsData, 'propertyKey'),
    )
    .value();
};

export type ConcentrationLimitFormatMap = Map<
  string,
  CreditAgreement.Config.ConcentrationLimit.Value.ValueType
>;

/**
 * @param concentrationInsights from concentration GQL query
 * @returns a Map of each concentration property to its valueType.
 */
export const getConcentrationLimitFormatsByKey = (
  concentrationInsights: ConcentrationLimitAggregateInsightsGqlDataGQLType[],
): ConcentrationLimitFormatMap => {
  return concentrationInsights.reduce<ConcentrationLimitFormatMap>(
    (keyToFormatMap, { concentrationLimitsData }) => {
      for (const { propertyKey, valueType } of concentrationLimitsData) {
        keyToFormatMap.set(propertyKey, valueType);
      }
      return keyToFormatMap;
    },
    new Map(),
  );
};

// ***********************************
// *** EXPENSES & FEES CHART UTILS ***
// ***********************************

export const getTAccountTypesFromLedgerData = (
  transactions: LedgerTransactionGQLType[],
): TAccountTypeGQLType[] => {
  const accountsSet = new Set<TAccountTypeGQLType>();
  _.forEach(transactions, ({ accountType }) => accountsSet.add(accountType));
  return Array.from(accountsSet);
};

export type StatementsInsight = {
  reportingDate: Util.FullDateStr;
} & Partial<Record<TAccountTypeGQLType, number>>;

export const formatLedgerToChartData = (
  transactions: LedgerTransactionGQLType[],
): StatementsInsight[] => {
  const groupedTransactionsByAccount = _.groupBy(
    transactions,
    ({ accountType }) => accountType,
  );

  const groupedTransactionsWithAccountTypeBalance = _.map(
    groupedTransactionsByAccount,
    (transactionsByAccount) => {
      const ascendingTransactions = _.sortBy(
        transactionsByAccount,
        'postingDate',
        'asc',
      );

      let accountTypeBalance = 0;
      return _.reduce(
        ascendingTransactions,
        (acc, entry) => {
          const { accountType, normalizedAmount, balance, postingDate } = entry;

          accountTypeBalance += normalizedAmount;

          return [
            ...acc,
            {
              postingDate,
              overallBalance: balance,
              accountType,
              accountTypeBalance,
            },
          ];
        },
        [] as {
          postingDate: Util.FullDateStr;
          overallBalance: number;
          accountType: TAccountTypeGQLType;
          accountTypeBalance: number;
        }[],
      );
    },
  );

  const groupedTransactionsByDate = _.chain(
    groupedTransactionsWithAccountTypeBalance,
  )
    .flatten()
    .sortBy('postingDate', 'asc')
    .groupBy('postingDate')
    .value();

  const balanceForAllAccountsByDay = _.map(
    groupedTransactionsByDate,
    (transactionsForDate, postingDate) => {
      const chartDataForDate: StatementsInsight = {
        reportingDate: postingDate as Util.FullDateStr,
      };

      _.forEach(transactionsForDate, (transaction) => {
        chartDataForDate[transaction.accountType] =
          transaction.accountTypeBalance;
      });

      return chartDataForDate;
    },
  );

  return balanceForAllAccountsByDay;
};

// ***************************
// *** VINTAGE CHART UTILS ***
// ***************************

export const findVintageChartDataForCohortSeriesAge = <
  T extends { cohortSeriesAge: number; [key: string]: Util.Primitive }
>(
  chartData: T[],
  cohortSeriesAge: number,
): T | undefined => {
  return _.find(
    chartData,
    (chartDatapoint) => chartDatapoint.cohortSeriesAge === cohortSeriesAge,
  );
};

export type ProfitsVintageInsight = {
  cohortSeriesAge: number;
  [key: Util.FullDateStr]: Util.Primitive;
};

export type ProfitsVintageTableRow = {
  originationDate: Util.FullDateStr;
  color: string;
  [key: string]: Util.Primitive;
};

export const formatProfitVintageToChartData = (
  profitVintageData: commonAnalytics.cohort.CohortGeneratedDataType<Insights.ProfitGeneratedCohortInsightsData>[],
  field: keyof Insights.ProfitGeneratedCohortInsightsData,
): ProfitsVintageInsight[] => {
  const flattenedData = _.flatMap(
    profitVintageData,
    (baseChartDatapoint) => baseChartDatapoint.cohortGeneratedData,
  );
  const groupedByCohortSeriesAge = _.groupBy(
    flattenedData,
    ({ cohortSeriesAge }) => cohortSeriesAge,
  );

  const withCohortBaseDate = _.map(
    groupedByCohortSeriesAge,
    (cohortSeriesAgeGroup) => {
      // already grouped by cohortSeriesAge, but we cannot use the key
      // because the key does not retain type since it is an object key
      // which has been turned into a string
      const cohortSeriesAgeNumber = cohortSeriesAgeGroup[0].cohortSeriesAge;
      const fieldDataForCohortSeriesAgeGroup: {
        cohortSeriesAge: number;
        [key: Util.FullDateStr]: Util.Primitive;
      } = { cohortSeriesAge: cohortSeriesAgeNumber };

      _.forEach(cohortSeriesAgeGroup, (datapoint) => {
        fieldDataForCohortSeriesAgeGroup[datapoint.cohortTruncatedBaseDate] =
          datapoint[field];
      });

      return fieldDataForCohortSeriesAgeGroup;
    },
  );

  return withCohortBaseDate;
};

export enum CohortCadence {
  week = 'week',
  month = 'month',
  quarter = 'quarter',
  year = 'year',
}
export const defaultCohortCadence = CohortCadence.month;
export type CohortConfig = {
  cohortBaseCadence: CohortCadence;
  cohortSeriesCadence: CohortCadence;
};
export const defaultMaxSeriesAge = 12;

export const cohortSeriesCadenceLabelFormatter = (
  cohortSeriesCadence: CohortCadence,
) => {
  return `${_.startCase(cohortSeriesCadence)}s since origination`;
};

export const cohortCadenceLabelFormatter = (
  cohortCadence: CohortCadence,
  cohortBaseDate: Util.FullDateStr,
): string => {
  const year = commonDate.fullDateStrToDate(cohortBaseDate).getFullYear();

  switch (cohortCadence) {
    case CohortCadence.week: {
      const cohortBaseDateTime = commonDate.fullDateStrToDateTime(
        cohortBaseDate,
      );
      const startOfWeek = cohortBaseDateTime.startOf('week').toFormat('MMM d');
      const endOfWeek = cohortBaseDateTime.endOf('week').toFormat('MMM d');
      return `${startOfWeek} - ${endOfWeek} ${year}`;
    }
    case CohortCadence.month: {
      const month = commonDate.fullDateStrToDateTime(cohortBaseDate).monthLong;
      return `${month} ${year}`;
    }
    case CohortCadence.quarter: {
      const quarter = commonDate.fullDateStrToQuarter(cohortBaseDate);
      return `Q${quarter} ${year}`;
    }
    case CohortCadence.year: {
      return `${year}`;
    }
    default:
      return cohortBaseDate;
  }
};

export const generatePortfolioVintageChartConfig = (
  cohortTruncatedBaseDates: Util.FullDateStr[],
  cohortBaseCadence: CohortCadence,
): PortfolioChartConfig<string> => {
  const lineColors = [...Object.values(colors.chart)];

  const portfolioVintageChartConfigs: Partial<
    Record<Util.FullDateStr, PortfolioSingleChartConfig>
  > = {};

  let lineColorIndex = 0;

  _.forEach(cohortTruncatedBaseDates, (cohortBaseDate) => {
    const label = cohortCadenceLabelFormatter(
      cohortBaseCadence,
      cohortBaseDate,
    );
    portfolioVintageChartConfigs[cohortBaseDate] = {
      chartType: 'line',
      field: cohortBaseDate,
      label,
      color: `${lineColors[lineColorIndex] ?? colors.brick[300]}${
        transparency[50]
      }`,
      format: 'percentage',
    };
    lineColorIndex =
      lineColorIndex + 1 === lineColors.length ? 0 : lineColorIndex + 1;
  });

  return portfolioVintageChartConfigs;
};

export const formatCohortCadenceSelectOptions = (): {
  label: string;
  value: CohortCadence;
}[] => {
  return _.map(CohortCadence, (cadence) => {
    return {
      label: `${_.startCase(cadence)}s`,
      value: cadence,
    };
  });
};

export const formatChartDataByCohortSeriesAge = <
  T extends { cohortSeriesAge: number; [key: string]: Util.Primitive }
>({
  chartData,
  selectedMaxSeriesAge,
}: {
  chartData: T[];
  selectedMaxSeriesAge: number;
}): T[] => {
  const orderedChartData: T[] = _.orderBy(chartData, 'cohortSeriesAge', 'desc');

  const vintageDataMap: _.Dictionary<T> = _.keyBy(
    orderedChartData,
    ({ cohortSeriesAge }) => cohortSeriesAge,
  );

  _.forEach(vintageDataMap, (_data, seriesAge) => {
    if (_.toNumber(seriesAge) > selectedMaxSeriesAge) {
      delete vintageDataMap[seriesAge];
    }
  });

  return Object.values(vintageDataMap);
};

export const onPortfolioVintageChartFilterUpdate = ({
  profitVintageInputDataForDateRange,
  selectedCohortConfig,
  selectedMaxSeriesAge,
  field,
  setChartConfig,
  setActiveFields,
  setChartDataWithCohortCadence,
  setTableDataByOriginationDate,
  setSelectedDatapoint,
}: {
  profitVintageInputDataForDateRange: CreditFacilityProfitVintageInputInsightsGqlDataGQLType[];
  selectedCohortConfig: CohortConfig;
  selectedMaxSeriesAge: number;
  field: keyof Insights.ProfitGeneratedCohortInsightsData;
  setChartConfig: React.Dispatch<
    React.SetStateAction<Partial<Record<string, PortfolioSingleChartConfig>>>
  >;
  setActiveFields: React.Dispatch<
    React.SetStateAction<Partial<keyof ProfitsVintageInsight>[]>
  >;
  setChartDataWithCohortCadence: React.Dispatch<
    React.SetStateAction<ProfitsVintageInsight[]>
  >;
  setTableDataByOriginationDate: React.Dispatch<
    React.SetStateAction<ProfitsVintageTableRow[]>
  >;
  setSelectedDatapoint: React.Dispatch<
    React.SetStateAction<ProfitsVintageInsight | null>
  >;
}) => {
  const dataByCohortCadence = commonAnalytics.cohort.generateCohortData(
    profitVintageInputDataForDateRange as Insights.CreditFacilityProfitVintageInputInsightsData[],
    {
      base: selectedCohortConfig.cohortBaseCadence,
      series: selectedCohortConfig.cohortSeriesCadence,
    },
    commonAnalytics.cohort.accumulateFn.profitInsightsAccumulator,
  );

  const chartConfigFromData = generatePortfolioVintageChartConfig(
    _.map(
      dataByCohortCadence,
      (datapoint) => datapoint.cohortTruncatedBaseDate,
    ),
    selectedCohortConfig.cohortBaseCadence,
  );

  setChartConfig(chartConfigFromData);

  const chartData = formatProfitVintageToChartData(dataByCohortCadence, field);
  const formattedChartData = formatChartDataByCohortSeriesAge<ProfitsVintageInsight>(
    {
      chartData,
      selectedMaxSeriesAge,
    },
  );

  const tableDataMap: {
    [originationDate: Util.FullDateStr]: ProfitsVintageTableRow;
  } = {};

  formattedChartData.forEach((cohort) => {
    const cohortSeriesAge = cohort.cohortSeriesAge;
    Object.entries(cohort).forEach(([key, value]) => {
      if (key !== 'cohortSeriesAge') {
        const tableRow = tableDataMap[key as Util.FullDateStr] ?? {
          originationDate: key,
          color: chartConfigFromData[key as Util.FullDateStr]?.color,
        };
        tableRow[
          `${cohortSeriesAge} ${selectedCohortConfig.cohortSeriesCadence}${
            cohortSeriesAge === 1 ? '' : 's'
          }`
        ] = value;
        tableDataMap[key as Util.FullDateStr] = tableRow;
      }
    });
  });

  setActiveFields(
    _.map(dataByCohortCadence, (cohort) => cohort.cohortTruncatedBaseDate),
  );

  setChartDataWithCohortCadence(formattedChartData);
  setTableDataByOriginationDate(
    _.orderBy(Object.values(tableDataMap), ['originationDate'], ['asc']),
  );
  setSelectedDatapoint(formattedChartData[formattedChartData.length - 1]);
};

// ************************
// *** RISK CHART UTILS ***
// ************************

export type AggregateBorrowingRiskChartData = Omit<
  AggregateBorrowingRiskInsightsGqlDataGQLType,
  'creditFacilityData'
>;

export const aggregateBorrowingBaseRiskDataForSelectedCreditFacilities = ({
  selectedCreditFacilityIds,
  dailyBorrowingBaseRiskData,
}: {
  selectedCreditFacilityIds: string[];
  dailyBorrowingBaseRiskData: AggregateBorrowingRiskInsightsGqlDataGQLType[];
}): AggregateBorrowingRiskChartData[] => {
  const borrowingBaseRiskDataMap: Record<
    Util.FullDateStr,
    Omit<AggregateBorrowingRiskInsightsGqlDataGQLType, 'creditFacilityData'>
  > = {};

  _.forEach(dailyBorrowingBaseRiskData, (dataForReportingDate) => {
    const { creditFacilityData } = dataForReportingDate;

    for (const dataForSingleFacility of creditFacilityData) {
      const {
        creditFacilityId,
        reportingDate,
        totalPerformingExposure,
        totalEligibleExposureLessConcentrationKickouts,
        totalCollectionsAccountCash,
        currentPrincipalBalance,
      } = dataForSingleFacility;

      if (_.includes(selectedCreditFacilityIds, creditFacilityId)) {
        if (!(reportingDate in borrowingBaseRiskDataMap)) {
          borrowingBaseRiskDataMap[reportingDate] = {
            reportingDate,
            totalPerformingExposure: 0,
            totalEligibleExposureLessConcentrationKickouts: 0,
            totalCollectionsAccountCash: 0,
            currentPrincipalBalance: 0,
            collateralCovenantLtv: 0,
            netOutstandingLtv: 0,
            performingLtv: 0,
          };
        }

        const targetData = borrowingBaseRiskDataMap[reportingDate];
        targetData.totalPerformingExposure += totalPerformingExposure;
        targetData.totalEligibleExposureLessConcentrationKickouts += totalEligibleExposureLessConcentrationKickouts;
        targetData.totalCollectionsAccountCash += totalCollectionsAccountCash;
        targetData.currentPrincipalBalance += currentPrincipalBalance;

        targetData.collateralCovenantLtv = commonUtils.divideByOrZero(
          targetData.currentPrincipalBalance,
          targetData.totalEligibleExposureLessConcentrationKickouts +
            targetData.totalCollectionsAccountCash,
        );
        targetData.netOutstandingLtv = commonUtils.divideByOrZero(
          targetData.currentPrincipalBalance -
            targetData.totalCollectionsAccountCash,
          targetData.totalEligibleExposureLessConcentrationKickouts +
            targetData.totalCollectionsAccountCash,
        );
        targetData.performingLtv = commonUtils.divideByOrZero(
          targetData.currentPrincipalBalance,
          targetData.totalPerformingExposure +
            targetData.totalCollectionsAccountCash,
        );
      }
    }
  });

  return Object.values(borrowingBaseRiskDataMap);
};
